import Vec2 from 'vec2';

import {
  ARROW_LENGTH_GRANULARITY_IN_INCHES,
  BACKSPLASH_INPUT_LIMIT,
  DEFAULT_CORNER_RADIUS,
  EDGE_LENGTH_INPUT_LIMIT,
  MINIMUM_ANGLE_TO_ENFORCE_GAP,
  MINIMUM_GAP,
  WATERFALL_INPUT_LIMIT,
} from '~/core/constants';
import { calculateIsoscelesTriangleSide } from '~/core/engine/DrawingTool/utils';
import { SideItemOption, isBumpOut, isRadiusOut } from '~/core/enums';
import {
  adjustEdgeForDiagonals,
  angleBetween,
  convertToRadians,
  findArrowByEdgeId,
  findDrawingEdgeById,
  isSameSign,
  roundToArrowLengthGranularity,
  roundToCornerRadiusGranularity,
  roundToNearest,
  subtractVectors,
} from '~/core/helpers';
import {
  ArrowInfo,
  CornerInfo,
  DrawingEdge,
  DrawingShape,
} from '~/core/models';
import { clampWithLimit } from '~/core/utils';

//Goes through the entire shape and calculates the minimum feasible edge lengths
export const calculateEdgeConstraints = (shape: DrawingShape): void => {
  if (!shape.arrowsInfo) {
    return;
  }
  // clearing any old constraints
  shape.arrowsInfo.forEach((arrow) => {
    arrow.maximumLength = undefined;
    arrow.minimumLength = undefined;
  });

  for (let i = 0; i < shape.arrowsInfo.length; i++) {
    const curArrow = shape.arrowsInfo[i];

    if (!curArrow.drawingEdgeId) {
      continue;
    }

    const curEdge = findDrawingEdgeById(curArrow.drawingEdgeId, shape);

    if (!curEdge || !curArrow?.size) {
      continue;
    }

    const skIndex = getSkeletonIndex(shape, curEdge);
    const parallelEdge = findParallelEdge(shape, curEdge);

    if (!parallelEdge) {
      continue;
    }

    const parallelArrow = findArrowByEdgeId(parallelEdge.id, shape);

    if (!parallelArrow || !parallelArrow.size) {
      continue;
    }

    const dif = roundToNearest(
      curArrow.size - parallelArrow.size,
      ARROW_LENGTH_GRANULARITY_IN_INCHES
    );

    let enforceMinGap = false;

    //if edge is not beginning or end of skeleton
    if (skIndex !== 0 && skIndex < shape.skeleton.length - 2) {
      // if the angle of the preceding side to the succeeding side is less than
      // MINIMUM_ANGLE_TO_ENFORCE_GAP, enforce the gap.
      const precedingSide = subtractVectors(
        shape.skeleton[skIndex].pos,
        shape.skeleton[skIndex - 1].pos
      );
      const succeedingSide = subtractVectors(
        shape.skeleton[skIndex + 1].pos,
        shape.skeleton[skIndex + 2].pos
      );

      if (
        angleBetween(precedingSide, succeedingSide) <
        MINIMUM_ANGLE_TO_ENFORCE_GAP
      ) {
        enforceMinGap = true;
      }
    }

    if (enforceMinGap) {
      curArrow.minimumLength = dif + MINIMUM_GAP;
      parallelArrow.minimumLength =
        MINIMUM_GAP - adjustEdgeForDiagonals(shape, parallelEdge);
    } else {
      curArrow.minimumLength = dif + EDGE_LENGTH_INPUT_LIMIT.MIN;
    }

    curArrow.maximumLength = calculateEdgeMaximums(shape, curEdge);
  }
};

const getSkeletonIndex = (shape: DrawingShape, edge: DrawingEdge) => {
  const startCorner = shape.cornerMap[edge.startCornerId];
  const endCorner = shape.cornerMap[edge.endCornerId];

  if (startCorner.isUpper) {
    return startCorner.skIndex;
  } else {
    return endCorner.skIndex;
  }
};

const calculateEdgeMaximums = (shape: DrawingShape, edge: DrawingEdge) => {
  if (!shape || !shape.drawingEdges) {
    return undefined;
  }
  const edgeSkIndex = getSkeletonIndex(shape, edge);
  const startCord = shape.cornerMap[edge.startCornerId].coords;
  const endCord = shape.cornerMap[edge.endCornerId].coords;

  // Determining if the edge is at the end of a skeleton. If the edge is not part of the last skeleton length, there are no maximums to apply
  if (edgeSkIndex !== 0 && edgeSkIndex !== shape.skeleton.length - 2) {
    return undefined;
  }

  const isBeginningOfSkeleton = edgeSkIndex === 0;

  let clearance;

  for (let i = 0; i < shape.drawingEdges.length; i++) {
    //Excludes edges that are not at least 3 skeleton lengths away
    const testEdge = shape.drawingEdges[i];

    if (
      Math.abs(getSkeletonIndex(shape, shape.drawingEdges[i]) - edgeSkIndex) < 3
    ) {
      continue;
    }

    //Builds a rectangular keep out zone for the test edge represented by the top right (tr) and bottom left (bl) corners
    const keepOutRect = buildKeepOutRectangle(testEdge, shape);

    // Removing massive if statement for vertical and horizontal edges with these functions
    let dirXy = (obj: Vec2) => roundToArrowLengthGranularity(obj.y);

    if (Math.abs(edge.dirVec.x) > Math.abs(edge.dirVec.y)) {
      // overwrite these functions if edge is horizontal
      dirXy = (obj: Vec2) => roundToArrowLengthGranularity(obj.x);
    }

    //Finds the direction of the end or beginning of the skeleton and figures out whether it is going towards the test edge
    if (isBeginningOfSkeleton) {
      if (
        !isSameSign(
          dirXy(shape.skeleton[1].pos) - dirXy(shape.skeleton[0].pos),
          dirXy(shape.skeleton[0].pos) -
            dirXy(shape.cornerMap[testEdge.startCornerId].coords)
        )
      ) {
        continue;
      }
    } else if (
      !isSameSign(
        dirXy(shape.skeleton[shape.skeleton.length - 2].pos) -
          dirXy(shape.skeleton[shape.skeleton.length - 1].pos),
        dirXy(shape.skeleton[shape.skeleton.length - 1].pos) -
          dirXy(shape.cornerMap[testEdge.startCornerId].coords)
      )
    ) {
      continue;
    }

    //if horizontal edge is on a different y than the keep out zone, there exist no possible collisions.
    if (Math.abs(edge.dirVec.x) > Math.abs(edge.dirVec.y)) {
      if (startCord.y < keepOutRect.tr.y || startCord.y > keepOutRect.bl.y) {
        continue;
      }
    } else if (
      startCord.x < keepOutRect.bl.x ||
      startCord.x > keepOutRect.tr.x
    ) {
      continue;
    }

    //Find the distance from both edges of the keep out rectangle from both the start and end coordinates
    const distanceFromStartCord = Math.min(
      Math.abs(dirXy(startCord) - dirXy(keepOutRect.tr)),
      Math.abs(dirXy(startCord) - dirXy(keepOutRect.bl))
    );
    const distanceFromEndCord = Math.min(
      Math.abs(dirXy(endCord) - dirXy(keepOutRect.tr)),
      Math.abs(dirXy(endCord) - dirXy(keepOutRect.bl))
    );

    const dist = Math.max(distanceFromEndCord, distanceFromStartCord);

    if (clearance) {
      clearance = Math.min(dist, clearance);
    } else if (dist) {
      clearance = dist;
    }
  }

  return clearance;
};

const buildKeepOutRectangle = (edge: DrawingEdge, shape: DrawingShape) => {
  const rect = { tr: new Vec2(0, 0), bl: new Vec2(0, 0) };
  const startCord = shape.cornerMap[edge.startCornerId].coords;
  const endCord = shape.cornerMap[edge.endCornerId].coords;

  if (Math.abs(edge.dirVec.x) > Math.abs(edge.dirVec.y)) {
    //edge is horizontal
    if (edge.dirVec.x > 0) {
      rect.tr = new Vec2(endCord.x + MINIMUM_GAP, endCord.y - MINIMUM_GAP);
      rect.bl = new Vec2(startCord.x - MINIMUM_GAP, startCord.y + MINIMUM_GAP);
    } else {
      rect.tr = new Vec2(startCord.x + MINIMUM_GAP, startCord.y - MINIMUM_GAP);
      rect.bl = new Vec2(endCord.x - MINIMUM_GAP, endCord.y + MINIMUM_GAP);
    }
  } else if (edge.dirVec.y > 0) {
    rect.tr = new Vec2(startCord.x + MINIMUM_GAP, startCord.y - MINIMUM_GAP);
    rect.bl = new Vec2(endCord.x - MINIMUM_GAP, endCord.y + MINIMUM_GAP);
  } else {
    rect.tr = new Vec2(endCord.x + MINIMUM_GAP, endCord.y - MINIMUM_GAP);
    rect.bl = new Vec2(startCord.x - MINIMUM_GAP, startCord.y + MINIMUM_GAP);
  }

  return rect;
};

//finds the opposing edge of the shape from the same skeleton
const findParallelEdge = (
  shape: DrawingShape,
  edge: DrawingEdge
): DrawingEdge | undefined => {
  const skStartIndex = shape.cornerMap[edge.startCornerId].skIndex;
  const skEndIndex = shape.cornerMap[edge.endCornerId].skIndex;

  return shape.drawingEdges.find(
    (testEdge) =>
      testEdge !== edge &&
      shape.cornerMap[testEdge.startCornerId].skIndex === skEndIndex &&
      shape.cornerMap[testEdge.endCornerId].skIndex === skStartIndex
  );
};

interface enforceEdgeConstraintsParams {
  arrow: ArrowInfo;
  value: number;
  edgeType?: SideItemOption;
  shape: DrawingShape;
}

/**
 * Returns the sum of the adjustments to the maximum length for the given edge due to adjacent side widths
 * @param shape the shape we're dealing with (to get the cornerMap)
 * @param edge the edge in question
 */
export const getAdjacentSideWidths = (
  shape: DrawingShape,
  edge?: DrawingEdge
): number => {
  if (!edge) {
    return 0;
  }

  return Object.values(shape.cornerMap)
    .filter((corner) =>
      [edge.startCornerId, edge.endCornerId].includes(corner.id)
    )
    .sort((a, b) => a.skIndex - b.skIndex)
    .reduce((acc, corner, index) => {
      if (!corner.angle || corner.angle >= 0) {
        return acc;
      }

      // adjust for the lower index bone
      const adjacentSkIndex = corner.skIndex - (index === 0 ? 1 : 0);

      return acc + shape.skeleton[adjacentSkIndex].depth * 2;
    }, 0);
};

/**
 * Returns the sum of the adjustments to the maximum length for the given edge due to diagonal corners
 * @param shape the shape we're dealing with (to get the cornerMap)
 * @param edge the edge in question
 */
export const getDiagonalAdjustmentsForEdge = (
  shape: DrawingShape,
  edge?: DrawingEdge
): number => {
  if (!edge) {
    return 0;
  }

  const corners = Object.values(shape.cornerMap);

  return [edge.startCornerId, edge.endCornerId].reduce((acc, id) => {
    const corner = corners.find((c) => c.id === id);
    const cornerAdjust = corner?.diagonalLength
      ? corner.diagonalLength / Math.sqrt(2)
      : 0;

    return acc + cornerAdjust;
  }, 0);
};

const getAdjustedEdgeValueByEdgeType = ({
  arrow,
  value = 0,
  edgeType,
  shape,
}: enforceEdgeConstraintsParams): number => {
  if (edgeType && !arrow.label) {
    const limitBy: Record<SideItemOption, typeof BACKSPLASH_INPUT_LIMIT> = {
      [SideItemOption.BACKSPLASH]: BACKSPLASH_INPUT_LIMIT,
      [SideItemOption.WATERFALL]: WATERFALL_INPUT_LIMIT,
    };

    return clampWithLimit(value, limitBy[edgeType]);
  }

  const edge = shape.drawingEdges.find(
    (edge) => edge.id === arrow.drawingEdgeId
  );
  const adjacentSideWidths = getAdjacentSideWidths(shape, edge);
  const diagonalLength = getDiagonalAdjustmentsForEdge(shape, edge);
  const isRadiusOrBumpOrDiagonal =
    arrow.isRadiusOut || arrow.isBumpOut || arrow.isDiagonalCorner;

  return clampWithLimit(value, {
    MIN: isRadiusOrBumpOrDiagonal ? 0 : EDGE_LENGTH_INPUT_LIMIT.MIN,
    MAX: EDGE_LENGTH_INPUT_LIMIT.MAX - (adjacentSideWidths + diagonalLength),
  });
};

const applyArrowMinMaxConstraints = ({
  arrow,
  value,
}: enforceEdgeConstraintsParams): number =>
  clampWithLimit(value, {
    MIN: arrow.minimumLength ?? -Infinity,
    MAX: arrow.maximumLength ?? Infinity,
  });

export const enforceEdgeConstraints = ({
  arrow,
  value = 0,
  edgeType,
  shape,
}: enforceEdgeConstraintsParams): number =>
  roundToArrowLengthGranularity(
    applyArrowMinMaxConstraints({
      arrow,
      value: getAdjustedEdgeValueByEdgeType({ arrow, value, edgeType, shape }),
      shape,
    })
  );

export const enforceRadiusConstraints = (
  cornerInfo: CornerInfo,
  value = 0
): number => {
  if (value < DEFAULT_CORNER_RADIUS) {
    return DEFAULT_CORNER_RADIUS;
  }

  const maxAllowedRadius = cornerInfo.maxAllowedRadius ?? 0;

  if (!maxAllowedRadius) {
    console.error(
      'Tried to enforceRadiusConstraints without a maxAllowedRadius',
      cornerInfo
    );
  }

  const maxRadius = Math.min(value, maxAllowedRadius);

  return maxRadius < DEFAULT_CORNER_RADIUS
    ? DEFAULT_CORNER_RADIUS
    : roundToCornerRadiusGranularity(maxRadius); //round to the nearest quarter inch
};

// populates max allowed radius for a corner
export const calcMaxAllowedRadius = (
  shape: DrawingShape,
  corner: CornerInfo
): void => {
  // this needs lots of work for radius out and bump outs
  const incidentEdges = shape.drawingEdges.filter(
    (edge) => edge.startCornerId === corner.id || edge.endCornerId === corner.id
  );
  let maxAllowedRadius = Infinity;

  incidentEdges.forEach((edge) => {
    const oppositeCornerId =
      edge.startCornerId === corner.id ? edge.endCornerId : edge.startCornerId;
    const oppositeCorner = shape.cornerMap[oppositeCornerId];
    let radiusSize = 0;

    if (corner.size && (isBumpOut(edge) || isRadiusOut(edge))) {
      radiusSize = corner.size;
    } else {
      const limit = oppositeCorner.size
        ? maxAvailableForCornerOptions(oppositeCorner)
        : DEFAULT_CORNER_RADIUS;

      radiusSize = edge.dirVec.length() - limit;
    }

    maxAllowedRadius = Math.min(maxAllowedRadius, radiusSize);
  });

  corner.maxAllowedRadius = maxAllowedRadius;
};

/**
 * find the maximum available size of either a radius or diagonal corner
 * @param corner
 * @param radius
 */
export const maxAvailableForCornerOptions = (
  corner: CornerInfo,
  radius = 0
): number => {
  if (!corner.angle) {
    return 0;
  }

  const tanRadians = Math.tan(convertToRadians(corner.angle / 2));

  if (radius !== 0) {
    // the radius argument is used when validating a corner can take a particular radius value
    return Math.abs(radius / tanRadians);
  }

  if (corner.diagonalLength) {
    return calculateIsoscelesTriangleSide(corner.angle, corner.diagonalLength);
  }

  // if no radius argument is passed, the intent is in calculating preset constriants on radii
  if (corner.size) {
    return roundToCornerRadiusGranularity(Math.abs(corner.size / tanRadians));
  }

  return 0;
};
